import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AssistantAllButtonTypes, CodeFile } from "utils/interfaces";
import AssistantPopup from "components/Editor/assistant/AssistantPopup";
import { usePlatform } from "components/utils/PlatformContext";
import { useContext, useEffect, useRef, useState } from "react";
import { MinecraftEnvironmentContext } from "components/utils/Contexts";
import {
  MdCallReceived,
  MdClose,
  MdEast,
  MdOutlineAutoAwesome,
} from "react-icons/md";
import { AnimatePresence, motion } from "framer-motion";
import useElementSize from "hooks/useElementSize";
import GlowingButton from "components/Editor/utils/GlowingButton";
import Video from "components/Editor/utils/Video";

interface MinecraftVideoPreviewHeaderProps {
  name: string;
  video: string;
  AIAssistant: {
    name: string;
    img: string;
    greeting: string;
    intro: string;
  };
  activeItem: "editor" | "assistant";
  setActiveItem: React.Dispatch<React.SetStateAction<"assistant" | "editor">>;
  showAssistant: boolean;
  assistantPopupState:
    | "closed"
    | "popup"
    | "popupNoCode"
    | "expanded"
    | "fullscreen";
  setAssistantPopupState: React.Dispatch<
    React.SetStateAction<
      "closed" | "popup" | "popupNoCode" | "expanded" | "fullscreen"
    >
  >;
  assistantPopupClickedButton: AssistantAllButtonTypes;
  setAssistantPopupClickedButton: React.Dispatch<
    React.SetStateAction<AssistantAllButtonTypes>
  >;
  codeFiles: CodeFile[];
}

const MinecraftVideoPreviewHeader: React.FC<
  MinecraftVideoPreviewHeaderProps
> = ({
  name,
  video,
  AIAssistant,
  activeItem,
  setActiveItem,
  showAssistant,
  assistantPopupState,
  setAssistantPopupState,
  assistantPopupClickedButton,
  setAssistantPopupClickedButton,
  codeFiles,
}) => {
  // State for controlling the onboarding glowing feature
  const [videoThumbnail, setVideoThumbnail] = useState("");
  const [onboardGlowing, setOnboardGlowing] = useState(true);
  const [showVideo, setShowVideo] = useState(false);

  // Get the platform context
  const { logo } = usePlatform();

  // Get the context values
  const { HEADER_HEIGHT } = useContext(MinecraftEnvironmentContext);

  // Define a navigator hook
  const navigate = useNavigate();

  // Get the parameters from the url
  const { moduleOrProject } = useParams();
  // Get a state if we are receiving a state variable from another url
  const { state } = useLocation();

  // Define a reference for the video iframe
  const videoIframeRef = useRef<HTMLIFrameElement>(null);
  // Define a reference for the player in order to avoid memory leaks
  const playerRef = useRef<any>(null);

  // Control the size of the video when resizing
  const [containerSize, imgContainerRef] = useElementSize<HTMLDivElement>();

  // Variables
  const hideVideo = activeItem === "assistant";

  const videoExpanded = false;

  useEffect(() => {
    if (videoExpanded) {
      const timer = setTimeout(() => {
        setShowVideo(true);
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setShowVideo(false);
    }
  }, [videoExpanded]);

  /**
   * Function used for fetching video title and thumbnail
   */
  useEffect(() => {
    // Check if the video is defined
    // if (video) {
    //   fetchVideoData(video).then((imageUrl: string) => {
    //     setVideoThumbnail(imageUrl);
    //   });
    // }
    setVideoThumbnail("/demoThumbnail.png");
  }, [video]);

  // Return to home url
  const goToDashboard = () => {
    if (state) {
      navigate("/home", {
        state: {
          // State for SuperAdmin.tsx
          activeItem: "lessons",
          // State for Lessons.tsx
          activeLesson: moduleOrProject,
          navRegister: state ? state.navRegister : [],
        },
      });
    } else {
      navigate("/home");
    }
  };

  /**
   * Function that defines the contextual behaviour of the back button
   */
  const handleClickBack = () => {
    if (activeItem === "assistant") setActiveItem("editor");
    else if (activeItem === "editor")
      navigate("/minecraft-editor/projects/demo");
  };

  /**
   * Function executed when clicking the "Show me around button"
   */
  const handleShowMeAroundClick = () => {
    // // First of all, check if the gui is ready to execute this
    // if (!guiReady) return;

    // De-activate the glowing
    setOnboardGlowing(false);

    // // Try to get the container pos to ensure that the "instructions button"
    // // is moving to the beginning of the container
    // let containerPos;
    // if (buttonsContainerRef.current) {
    //   // Set the instruction button position for the animation
    //   const { x, y } = buttonsContainerRef.current.getBoundingClientRect();
    //   // And set the anchor position
    //   containerPos = { x, y };
    // }

    // // Force closing the instructions
    // if (instructionsButtonRef.current) {
    //   // And close the PDF panel
    //   instructionsButtonRef.current.handleClosePDFPanel(containerPos);
    // }

    // // Force closing the video
    // setVideoExpanded(false);

    // // Activate the onboarding pop up
    // setShowOnboarding(true);
  };

  const handleThumbnailClick = () => {
    // Start pausing the video
    pauseVideo();

    // // Expanding the video
    // setVideoExpanded(!videoExpanded);
  };

  /**
   * Function used to pause video
   */
  const pauseVideo = () => {
    if (playerRef.current) {
      const player = playerRef.current;
      player.pause();
    }
  };

  return (
    <>
      <div
        className="w-full px-6 relative flex items-center justify-between z-20"
        style={{ height: `${HEADER_HEIGHT}px` }}
      >
        {/* Logo and back button */}
        <div className="min-w-[480px] left-10 flex items-center gap-8">
          <div className="max-w-[160px] h-20 flex items-center justify-center">
            <img
              className="h-full object-contain cursor-pointer"
              src={logo}
              onClick={goToDashboard}
              alt="Platform logo"
            />
          </div>
          <div className="w-px self-stretch bg-neutral-200" />
          <div className="w-full justify-start items-center gap-6 flex">
            <div
              id="minecraft-close-button"
              className="flex-col justify-start items-center gap-2 inline-flex"
            >
              <div
                className="w-12 h-12 p-3 bg-neutral-50 hover:bg-neutral-100 ease-in-out duration-150 rounded-full justify-center items-center inline-flex cursor-pointer z-10"
                onClick={handleClickBack}
              >
                <MdClose className="w-6 h-6 fill-secondary-600" />
              </div>
              <div className="justify-start items-start gap-2 inline-flex">
                <div className="text-center text-secondary-600 text-xs font-semibold font-sans uppercase leading-[14px] tracking-wide select-none">
                  Close
                </div>
              </div>
            </div>
            <div className="inline-flex flex-grow flex-col justify-center items-start gap-2 select-none">
              <h1 className="text-center text-neutral-600 text-[23px] font-bold font-sans leading-7">
                {activeItem === "assistant" ? "Feedback" : name}
              </h1>
            </div>
          </div>
        </div>

        {/* Assistant and video buttons */}
        <div className="right-10 flex items-center mr-3 z-10">
          {/* Conditional rendering for Assistant */}
          {showAssistant && (
            <AssistantPopup
              AIAssistant={AIAssistant}
              state={assistantPopupState}
              setState={setAssistantPopupState}
              clickedButton={assistantPopupClickedButton}
              setClickedButton={setAssistantPopupClickedButton}
              codeFiles={codeFiles}
              goToDashboard={goToDashboard}
              language="Minecraft"
            />
          )}
        </div>

        <div
          id="minecraft-close-button"
          className="flex-col justify-start items-center gap-2 inline-flex"
        >
          <div
            className="w-12 h-12 p-3 bg-secondary-600 hover:bg-secondary-700 ease-in-out duration-150 rounded-full justify-center items-center inline-flex cursor-pointer z-10"
            onClick={handleClickBack}
          >
            <MdEast className="w-6 h-6 fill-white" />
          </div>
          <div className="justify-start items-start gap-2 inline-flex">
            <div className="text-center text-secondary-600 text-xs font-semibold font-sans uppercase leading-[14px] tracking-wide select-none">
              Start activity
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MinecraftVideoPreviewHeader;
